import { ColumnDef } from '@tanstack/react-table'
import { MoreHorizontal, Trash2, ArrowUp } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useState, useRef, useEffect, memo } from 'react'
import type { Layer } from '@/types/entities'
import { TranslatableText } from '@/components/ui/TranslatableText'
import { getResponsiveColumnSize } from '@/lib/utils/responsive-columns'

interface LayerActionsProps {
    layer: Layer
    onDelete: (layer: Layer) => void
    onInsert: (layer: Layer) => void
}

function LayerActions({ layer, onDelete, onInsert }: LayerActionsProps) {
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)

    return (
        <div
            className="flex justify-center"
            onClick={(e) => {
                // Prevent table row click handlers
                e.stopPropagation()
            }}
            onMouseDown={(e) => {
                // Prevent table focus changes
                e.stopPropagation()
            }}
            onMouseUp={(e) => {
                e.stopPropagation()
            }}
            onPointerDown={(e) => {
                e.stopPropagation()
            }}
            onPointerUp={(e) => {
                e.stopPropagation()
            }}
        >
            <DropdownMenu open={open} onOpenChange={setOpen} modal={true}>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        onClick={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                            setOpen(true)
                        }}
                        onMouseDown={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                        }}
                        onMouseUp={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                        }}
                    >
                        <span className="sr-only">{t('layers.openMenu')}</span>
                        <MoreHorizontal className="h-4 w-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    align="end"
                    side="bottom"
                    sideOffset={5}
                    className="w-56"
                    onCloseAutoFocus={(e) => {
                        // Prevent focus from jumping back into table
                        e.preventDefault()
                    }}
                >
                    <DropdownMenuItem
                        onClick={(e) => {
                            e.stopPropagation()
                            onInsert(layer)
                            setOpen(false)
                        }}
                    >
                        <ArrowUp className="mr-2 h-4 w-4" />
                        {t('layers.insertAbove')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={(e) => {
                            e.stopPropagation()
                            onDelete(layer)
                            setOpen(false)
                        }}
                    >
                        <Trash2 className="mr-2 h-4 w-4" />
                        {t('layers.deleteLayer')}
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    )
}

// Inline editable description cell
const EditableDescriptionCell = memo(function EditableDescriptionCell({
    layer,
    onUpdate
}: {
    layer: Layer
    onUpdate: (layer: Layer, description: string) => void
}) {
    const [isEditing, setIsEditing] = useState(false)
    const [value, setValue] = useState(layer.description || '')
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus()
            inputRef.current.select()
        }
    }, [isEditing])

    useEffect(() => {
        setValue(layer.description || '')
    }, [layer.description])

    const handleSave = () => {
        if (value !== (layer.description || '')) {
            onUpdate(layer, value)
        }
        setIsEditing(false)
    }

    const handleCancel = () => {
        setValue(layer.description || '')
        setIsEditing(false)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            handleSave()
        } else if (e.key === 'Escape') {
            e.preventDefault()
            handleCancel()
        }
    }

    return (
        <div
            className="w-full"
            onClick={(e) => {
                if (!isEditing) {
                    e.stopPropagation()
                    setIsEditing(true)
                }
            }}
            onMouseDown={(e) => {
                e.stopPropagation()
            }}
        >
            {isEditing ? (
                <Input
                    ref={inputRef}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onBlur={handleSave}
                    onKeyDown={handleKeyDown}
                    className="h-8 w-full text-sm"
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                />
            ) : (
                <div
                    className="h-8 w-full rounded-md border border-input bg-background px-3 py-2 text-sm flex items-center cursor-text hover:border-ring transition-colors"
                    style={{ minWidth: 0 }}
                >
                    <span className="text-sm truncate w-full">{layer.description ? <TranslatableText text={layer.description} /> : '-'}</span>
                </div>
            )}
        </div>
    )
})

export function createLayerColumns(
    onDelete: (layer: Layer) => void,
    onInsert: (layer: Layer) => void,
    onUpdateDescription: (layer: Layer, description: string) => void,
    t: (key: string) => string
): ColumnDef<Layer>[] {
    // Calculate responsive column sizes
    const columnSizes = {
        layerID: getResponsiveColumnSize(80),
        description: getResponsiveColumnSize(250),
        noObjects: getResponsiveColumnSize(130),
        actions: 80  // Fixed width
    }

    return [
        {
            accessorKey: 'layerID',
            header: t('layers.layerID'),
            size: columnSizes.layerID,
            minSize: 70,
            maxSize: 130,
            cell: ({ row }) => {
                const layerID = row.getValue('layerID') as number | null
                return (
                    <div className="font-medium">
                        {layerID !== null ? layerID : '-'}
                    </div>
                )
            },
        },
        {
            accessorKey: 'backgroundColor',
            header: t('layers.color', 'Color'),
            size: 60,
            minSize: 50,
            maxSize: 80,
            cell: ({ row }) => {
                const bgColor = (row.original as Layer & { backgroundColor?: string }).backgroundColor
                return (
                    <div className="flex justify-center">
                        <div
                            style={{
                                width: 20,
                                height: 20,
                                borderRadius: 4,
                                backgroundColor: bgColor || '#E0E0E0',
                                border: '1px solid #ccc',
                            }}
                        />
                    </div>
                )
            },
        },
        {
            accessorKey: 'description',
            header: t('layers.description'),
            size: columnSizes.description,
            minSize: 200,
            maxSize: 400,
            cell: ({ row }) => {
                const layer = row.original
                return (
                    <div
                        className="w-full"
                        onClick={(e) => {
                            // Prevent table row click handlers
                            e.stopPropagation()
                        }}
                        onMouseDown={(e) => {
                            // Prevent table focus changes
                            e.stopPropagation()
                        }}
                    >
                        <EditableDescriptionCell
                            layer={layer}
                            onUpdate={onUpdateDescription}
                        />
                    </div>
                )
            },
        },
        {
            accessorKey: 'noObjects',
            header: t('layers.numberOfObjects'),
            size: columnSizes.noObjects,
            minSize: 110,
            maxSize: 200,
            cell: ({ row }) => {
                const noObjects = row.getValue('noObjects') as number | undefined
                return (
                    <div className="text-center">
                        {noObjects !== undefined ? noObjects : 0}
                    </div>
                )
            },
        },
        {
            id: 'actions',
            size: columnSizes.actions,
            minSize: 80,
            maxSize: 80,
            header: () => (
                <div className="text-center w-full">
                    {t('common.actions')}
                </div>
            ),
            cell: ({ row }) => {
                const layer = row.original
                return (
                    <LayerActions
                        layer={layer}
                        onDelete={onDelete}
                        onInsert={onInsert}
                    />
                )
            },
        },
    ]
}

